import { Box, Heading } from '@chakra-ui/react';

import type { SelectionList } from '@revelio/iso-utility';

import type {
  CustomFilter,
  DataBuilderCustomColumn,
  SelectionListSelectedItem,
} from '../../data-builder.store';
import { formatSelectionSummary } from '../../utils/selection-summary';
import type { CompaniesData } from './CompaniesSummary';
import { CompaniesSummary } from './CompaniesSummary';
import { ConfigSummaryList } from './ConfigSummaryList';
import { CustomColumnsSummary } from './CustomColumnsSummary';
import { FiltersSummary } from './FiltersSummary';
import type { ModelVersionEntry } from './ModelVersionsSummary';
import { ModelVersionsSummary } from './ModelVersionsSummary';
import { SUMMARY_HEADING_PROPS } from './SummaryPrimitives';

export type DatasetSummaryColumn = {
  key: string;
  label: string;
};

export type DatasetSummarySetting = {
  label: string;
  displayValue: string;
};

/** Maps raw condition field keys to display labels, e.g. "role_k10" -> "Role" */
export type FieldLabelMap = Record<string, string>;

/** Maps raw condition field keys to short granularity suffixes, e.g. "role_k10" -> "k10" */
export type FieldGranularityMap = Record<string, string>;

export type FilterGroup = {
  breakdownLabel: string | null;
  filters: CustomFilter[];
  fieldLabelMap: FieldLabelMap;
  fieldGranularityMap: FieldGranularityMap;
};

export type CustomColumnGroup = {
  breakdownLabel: string | null;
  customColumns: DataBuilderCustomColumn[];
  fieldLabelMap: FieldLabelMap;
  fieldGranularityMap: FieldGranularityMap;
};

export type DatasetSummaryData = {
  instanceId: string;
  datasetKey: string;
  label: string;
  unitOfAnalysis: string;
  unitOfAnalysisSelections: SelectionListSelectedItem[];
  companies: CompaniesData;
  granularities: string[];
  columns: {
    breakdownId: string;
    breakdownLabel: string;
    columns: DatasetSummaryColumn[];
  }[];
  filterGroups: FilterGroup[];
  customColumnGroups: CustomColumnGroup[];
  settings: DatasetSummarySetting[];
  modelVersions: ModelVersionEntry[];
  allLatestModelVersions: boolean;
};

const buildColumnDetails = (columns: DatasetSummaryData['columns']) => {
  const allColumns = columns.flatMap(({ columns: cols }) => cols);

  if (allColumns.length === 0) {
    return [{ label: 'Columns', value: 'Default' as const }];
  }

  return columns.map(({ breakdownLabel, columns: cols }) => ({
    label: columns.length === 1 ? 'Columns' : `Columns (${breakdownLabel})`,
    value: cols.map((col) => col.label).join(', '),
  }));
};

const formatSettings = (settings: DatasetSummarySetting[]) =>
  settings.map((s) => `${s.label}: ${s.displayValue}`).join(', ');

const formatPrimaryUnitOfAnalysisSummary = (
  unitOfAnalysis: string,
  selections: SelectionListSelectedItem[]
) => {
  if (unitOfAnalysis === 'default') {
    return 'Default';
  }

  const selectionSummary = formatSelectionSummary({
    type: 'unit_of_analysis',
    selections,
  });

  if (selectionSummary === '') {
    return unitOfAnalysis;
  }

  return `${unitOfAnalysis} (${selectionSummary})`;
};

export const DatasetSummaryCard = ({
  dataset,
  selectionLists,
}: {
  dataset: DatasetSummaryData;
  selectionLists?: SelectionList[];
}) => {
  const unitOfAnalysisDisplay = formatPrimaryUnitOfAnalysisSummary(
    dataset.unitOfAnalysis,
    dataset.unitOfAnalysisSelections
  );

  const showCompanies = dataset.companies.type !== 'default';

  const details = [
    { label: 'Primary Unit of Analysis', value: unitOfAnalysisDisplay },
    ...(showCompanies
      ? [
          {
            label: 'Companies',
            value: <CompaniesSummary companies={dataset.companies} />,
          },
        ]
      : []),
    ...buildColumnDetails(dataset.columns),
    ...dataset.filterGroups.map((group) => ({
      label:
        group.breakdownLabel !== null
          ? `Filters (${group.breakdownLabel})`
          : 'Filters',
      value: (
        <FiltersSummary
          filters={group.filters}
          selectionLists={selectionLists}
          fieldLabelMap={group.fieldLabelMap}
          fieldGranularityMap={group.fieldGranularityMap}
        />
      ),
    })),
    ...dataset.customColumnGroups.map((group) => ({
      label:
        group.breakdownLabel !== null
          ? `Custom Columns (${group.breakdownLabel})`
          : 'Custom Columns',
      value: (
        <CustomColumnsSummary
          customColumns={group.customColumns}
          selectionLists={selectionLists}
          fieldLabelMap={group.fieldLabelMap}
          fieldGranularityMap={group.fieldGranularityMap}
        />
      ),
    })),
    ...(dataset.settings.length > 0
      ? [{ label: 'Settings', value: formatSettings(dataset.settings) }]
      : []),
    {
      label: 'Model Versions',
      value: (
        <ModelVersionsSummary
          entries={dataset.modelVersions}
          allLatest={dataset.allLatestModelVersions}
        />
      ),
    },
  ];

  return (
    <Box mb="2">
      <Heading {...SUMMARY_HEADING_PROPS} mt="2">
        {dataset.label}
      </Heading>
      <ConfigSummaryList details={details} />
    </Box>
  );
};
